import { useEffect, useState } from "react";
import { Sparkles, RefreshCw, TrendingDown, ChevronDown, ChevronRight, Loader2, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import DualValuationSparkline from "@/components/DualValuationSparkline";

/**
 * DualValuation — composant Brute / Net pour Origination Taille de marché
 * (V23.15.15 — module pilote, à répliquer sur Bras Armé / Générateur / Fractale).
 *
 * Affiche :
 *  - Valeur brute (badge gris, "Marché théorique total")
 *  - Valeur nette estimée (badge coloré selon confidence)
 *  - Liste des réductions (3-5 principales) avec deep-links
 *  - Bouton "Recalculer" + bouton "Recalculer avec narrative IA"
 *
 * Mode additif strict : ne remplace JAMAIS un chiffre brut existant. S'ajoute
 * en dessous ou à côté des stats existantes.
 */
const CONFIDENCE_COLORS = {
  high: { ring: "border-emerald-500/40", chip: "bg-emerald-500/15 text-emerald-300", label: "Fiabilité élevée" },
  medium: { ring: "border-[#EF9F27]/40", chip: "bg-[#EF9F27]/15 text-[#EF9F27]", label: "Fiabilité modérée" },
  low: { ring: "border-zinc-500/40", chip: "bg-zinc-500/15 text-zinc-400", label: "Fiabilité limitée" },
};

const SOURCE_DEEPLINKS = {
  friction_gap: "/app/friction-gap",
  phantom_detector: "/app/phantom-detector",
  doctrine_48: "/app/doctrine",
  annuaire: "/app/annuaire",
  tavily: "/app/radar",
};

export default function DualValuation({
  scope = "origination_market",
  currencyLabel = "M€",
  bruteLabel = "Marché théorique total",
  netteLabel = "Capturable réaliste compte tenu des frictions identifiées",
  testIdPrefix = "valuation-origination",
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showReductions, setShowReductions] = useState(true);

  const fmt = (n) =>
    n >= 1000
      ? `${(n / 1000).toFixed(1)} Mrd`
      : `${n.toFixed(1)}`;

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/workspace/valuations/origination`);
      setData(r.data);
    } catch (e) {
      notify.error(fmtError(e, "Chargement impossible"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const recompute = async (withNarrative = false) => {
    setBusy(true);
    try {
      const r = await api.post(
        `/workspace/valuations/origination/recompute?with_narrative=${withNarrative}`
      );
      setData(r.data);
      notify.success(withNarrative ? "Recalcul + narrative IA effectués" : "Recalcul effectué");
    } catch (e) {
      notify.error(fmtError(e, "Recalcul impossible"));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="noam-card p-5 text-zinc-400 text-sm inline-flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement de la valuation…
      </div>
    );
  }

  if (!data) return null;

  const confidence = data.confidence || "low";
  const cConf = CONFIDENCE_COLORS[confidence] || CONFIDENCE_COLORS.low;
  const brute = Number(data.brute_mEUR || 0);
  const nette = Number(data.nette_mEUR || 0);
  const neverComputed = data.never_computed === true;
  const reductionsCount = (data.reductions || []).length;
  const computedAt = data.computed_at ? new Date(data.computed_at).toLocaleString("fr-FR") : null;

  return (
    <div
      className={`noam-card p-5 ${cConf.ring} border`}
      data-testid={testIdPrefix}
    >
      <div className="flex items-start gap-4 flex-wrap">
        {/* Bloc Brute */}
        <div className="min-w-[180px]" data-testid={`${testIdPrefix}-brute`}>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">
            {bruteLabel}
          </div>
          <div className="noam-display text-3xl font-bold mt-1 text-zinc-200">
            {fmt(brute)} <span className="text-base text-zinc-500">{currencyLabel}</span>
          </div>
          <div className="text-[10px] text-zinc-600 mt-1">
            {data.deal_count ?? 0} deal(s) ouvert(s)
          </div>
        </div>

        {/* Flèche de réduction */}
        <div className="flex flex-col items-center justify-center pt-3 self-stretch">
          <TrendingDown className="w-4 h-4 text-zinc-500" />
          {brute > 0 && (
            <span className="text-[10px] text-zinc-500 mt-1">
              ×{(brute > 0 ? nette / brute : 0).toFixed(2)}
            </span>
          )}
        </div>

        {/* Bloc Nette */}
        <div className="min-w-[200px] flex-1" data-testid={`${testIdPrefix}-nette`}>
          <div className="text-[10px] uppercase tracking-wider text-[#EF9F27] inline-flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Valeur nette estimée
          </div>
          <div className="noam-display text-3xl font-bold mt-1 text-[#EF9F27]">
            {fmt(nette)} <span className="text-base text-[#EF9F27]/70">{currencyLabel}</span>
          </div>
          <div className="text-[10px] text-zinc-500 mt-1 leading-relaxed pr-3">
            {netteLabel}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col items-end gap-2">
          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${cConf.chip}`}>
            {cConf.label}
          </span>
          <button
            onClick={() => recompute(false)}
            disabled={busy}
            className="text-[11px] px-2.5 py-1 rounded-md bg-white/5 border border-white/10 text-zinc-200 hover:bg-white/10 inline-flex items-center gap-1 disabled:opacity-50"
            data-testid={`${testIdPrefix}-recompute-btn`}
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Recalculer
          </button>
          <button
            onClick={() => recompute(true)}
            disabled={busy}
            className="text-[10px] px-2 py-0.5 rounded-md text-[#378ADD] hover:underline disabled:opacity-50"
            data-testid={`${testIdPrefix}-recompute-narrative-btn`}
          >
            + narrative IA
          </button>
        </div>
      </div>

      {/* Bloc réductions */}
      {reductionsCount > 0 ? (
        <div className="mt-4 pt-4 border-t border-white/5">
          <button
            onClick={() => setShowReductions((v) => !v)}
            className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-zinc-400 hover:text-zinc-200"
            data-testid={`${testIdPrefix}-reductions-toggle`}
          >
            {showReductions ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            Pourquoi cet écart ? ({reductionsCount} friction(s) identifiée(s))
          </button>
          {showReductions && (
            <ul className="mt-2 space-y-1.5">
              {(data.reductions || []).map((r) => {
                const deepLink = SOURCE_DEEPLINKS[r.source];
                return (
                  <li
                    key={r.key}
                    className="text-[11px] text-zinc-300 flex items-start gap-2"
                    data-testid={`${testIdPrefix}-reduction-${r.key}`}
                  >
                    <span className="text-[#EF9F27] font-medium shrink-0">×{r.factor}</span>
                    <span className="flex-1">
                      <span className="font-medium text-zinc-200">{r.label}</span>
                      <span className="text-zinc-500"> — {r.justification}</span>
                    </span>
                    {deepLink && (
                      <a
                        href={deepLink}
                        className="text-[#378ADD] hover:underline inline-flex items-center gap-0.5 shrink-0"
                        data-testid={`${testIdPrefix}-reduction-link-${r.key}`}
                      >
                        Voir <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : (
        <div className="mt-4 pt-4 border-t border-white/5 text-[10px] text-zinc-500">
          {neverComputed
            ? "Aucun calcul effectué. Cliquez sur \"Recalculer\" pour générer la valuation."
            : "Aucune friction détectée — la valeur nette = la valeur brute."}
        </div>
      )}

      {data.narrative && (
        <div
          className="mt-3 p-3 rounded-md bg-[#378ADD]/10 border border-[#378ADD]/30 text-[11px] text-zinc-200 leading-relaxed"
          data-testid={`${testIdPrefix}-narrative`}
        >
          <div className="text-[9px] uppercase tracking-wider text-[#378ADD] mb-1">
            Justification Noam
          </div>
          {data.narrative}
        </div>
      )}

      {/* V23.15.16 — Sparkline historique 90j Brute vs Nette */}
      {!neverComputed && (
        <DualValuationSparkline scope="origination" days={90} testId={`${testIdPrefix}-history`} />
      )}

      {computedAt && (
        <div className="mt-2 text-[9px] text-zinc-600 italic">
          Dernière mise à jour : {computedAt}
        </div>
      )}
    </div>
  );
}
